import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import apiClient from '@/api/client'
import type { UseReportRange } from '@/hooks/useReportRange'
import { useCurrency } from '@/contexts/CurrencyContext'
import { ReportsExportSlot } from './ReportsExportSlot'
import { ExportButton } from './ExportButton'
import { formatDateRangeHuman } from '@/lib/utils'

interface Props {
  range: UseReportRange
}

/**
 * Tax tab — output tax by effective rate band + a three-way reconciliation
 * (charged vs submitted to FBR vs credit-note reversals). Business-day keyed;
 * ties out to the tax-reconciliation skill for the same period. Every order's
 * tax lands in exactly one band, so Σ(band output tax) === total output tax.
 */
export function TaxSummaryTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const query = useQuery({
    queryKey: ['reports-v2', 'tax', range.fromISO, range.toISO],
    queryFn: () => apiClient.getTaxSummary(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const data = query.data?.data
  const bands = data?.bands ?? []
  const rec = data?.reconciliation

  return (
    <div className="space-y-4">
      <ReportsExportSlot>
        <ExportButton
          report="tax"
          reportLabel="Tax summary"
          fromISO={range.fromISO}
          toISO={range.toISO}
        />
      </ReportsExportSlot>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Output Tax by Rate — {formatDateRangeHuman(range.from, range.to)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : bands.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No taxed sales in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5">Rate</th>
                    <th className="text-right px-4 py-2.5">Orders</th>
                    <th className="text-right px-4 py-2.5">Taxable Base</th>
                    <th className="text-right px-4 py-2.5">Output Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bands.map((b) => (
                    <tr key={b.rate} className="hover:bg-muted/40">
                      <td className="px-4 py-3 font-medium tabular-nums">{b.rate}%</td>
                      <td className="text-right tabular-nums px-4 py-3">
                        {b.orders.toLocaleString('en-US')}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3">
                        {formatCurrency(b.taxable_base)}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3 font-semibold">
                        {formatCurrency(b.output_tax)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/30 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {bands.reduce((s, b) => s + b.orders, 0).toLocaleString('en-US')}
                    </td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {formatCurrency(data?.total_taxable_base ?? 0)}
                    </td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {formatCurrency(data?.total_output_tax ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {rec && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">FBR Reconciliation</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <ReconLine label="Output tax charged (books)" value={formatCurrency(rec.charged_tax)} />
              <ReconLine
                label="Submitted to FBR"
                value={formatCurrency(rec.submitted_tax)}
                sub={`${rec.submitted_count.toLocaleString('en-US')} invoices`}
              />
              <ReconLine
                label="Δ Charged − Submitted"
                value={formatCurrency(rec.delta_charged_minus_c)}
                tone={Math.abs(rec.delta_charged_minus_c) > 0.5 ? 'warn' : 'ok'}
              />
              <ReconLine
                label="Credit notes (synced)"
                value={`−${formatCurrency(rec.credit_note_tax_synced)}`}
                sub={`${rec.credit_notes.toLocaleString('en-US')} notes`}
              />
              <ReconLine
                label="Net output tax"
                value={formatCurrency(rec.net_output_tax)}
                emphasise
              />
              <ReconLine
                label="Fiscal queue"
                value={`${rec.queue_pending} pending · ${rec.queue_dead} dead`}
                tone={rec.queue_dead > 0 ? 'warn' : 'ok'}
              />
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ReconLine({
  label,
  value,
  sub,
  tone,
  emphasise,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'ok' | 'warn'
  emphasise?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dashed border-muted py-1.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">
        <span
          className={`tabular-nums ${emphasise ? 'font-semibold text-base' : ''} ${
            tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
          }`}
        >
          {value}
        </span>
        {sub && <span className="ml-2 text-xs text-muted-foreground">{sub}</span>}
      </dd>
    </div>
  )
}
